/**
 * LaTeX math.
 *
 * Syntax (stored inside the note's markdown — this is the synced format):
 *   $latex$              → inline math
 *   $$                   → block (display) math; the closing `$$` sits on its
 *   latex                  own line. `$$latex$$` on a single line is also a
 *   $$                     block.
 *
 * The LaTeX source is stored verbatim (no markdown escaping inside the
 * delimiters) and rendered by KaTeX on the web / SwiftMath natively. This
 * module is the single source of truth for the delimiter rules; the web app
 * (markdown-it rule in `apps/app/src/editor/markdownItMath.ts`), the share
 * viewer (`remark-math`), the worker (plain-text strip), and the Swift editor
 * (`MathSyntax.swift`) all mirror this exact contract. **If you change a
 * regex, change it here** (and mirror it in Swift).
 */

/**
 * Matches `$latex$`.
 *
 * - The opening `$` must not be preceded by a backslash or another `$`, and
 *   must not be followed by whitespace or `$`.
 * - The closing `$` must not be preceded by whitespace or a backslash, and
 *   must not be followed by a digit or `$`, so prose like `costs $5 and $10`
 *   never turns into math (Pandoc's rule).
 * - Content never spans a line.
 *
 * Group 1 = the LaTeX source.
 */
export const MATH_INLINE_REGEX =
  /(?<![\\$])\$(?![\s$])([^$\n]+?)(?<![\s\\])\$(?![\d$])/g

/**
 * Matches a display-math block: an opening `$$` that starts a line, the
 * LaTeX (possibly multi-line), and a closing `$$` that ends a line.
 *
 * Group 1 = the LaTeX source (trim it — surrounding newlines are included).
 */
export const MATH_BLOCK_REGEX =
  /^[ \t]*\$\$[ \t]*\n?([\s\S]*?)\n?[ \t]*\$\$[ \t]*$/gm

/**
 * Remove math entirely from plain-text surfaces (previews, titles, push
 * bodies, search). Raw LaTeX reads as noise there (`\int_0^\infty e^{-x^2}`),
 * so the formula is dropped rather than unwrapped, and the horizontal
 * whitespace it leaves behind is collapsed. Mirrored by `stripMath` in
 * Swift's `MarkdownStripper.swift`.
 */
export function stripMath(text: string): string {
  return text
    .replace(MATH_BLOCK_REGEX, "")
    .replace(MATH_INLINE_REGEX, "")
    .replace(/[ \t]{2,}/g, " ")
}
